/**
 * Onboarding Suggest-Tests API
 *
 * Maps to real backend endpoints:
 *   - Site snapshot data: GET/POST /api/v1/property/:propertyId/onboarding
 *     (combined with heatmap page screenshots from GET /api/v1/heatmap-pages/:propertyId)
 *   - Launch test: POST /api/v1/experiments/:propertyId  (testType: "component")
 *   - Draft preview: PUT /api/v1/ab-tests/:testId  (saves instructions for preview)
 *   - Preview fetch: GET /api/v1/ab-tests/:testId/preview  (used by hmbl.js)
 *   - Experiment generation: POST/GET /api/v1/property/:propertyId/experiment-generation
 */

import type { ComponentInstruction } from "./api"
import { createApiError, parseResponseBody } from "./api"

const PUBLIC_IS_PROD = import.meta.env.VITE_PUBLIC_IS_PROD
const PUBLIC_IS_LOCAL = import.meta.env.VITE_PUBLIC_IS_LOCAL
const PUBLIC_API_URL = import.meta.env.VITE_PUBLIC_API_URL

const API_URL = PUBLIC_API_URL
  ? `${PUBLIC_API_URL}/api/v1/`
  : PUBLIC_IS_PROD === "TRUE"
  ? `https://app.humblytics.com/api/v1/`
  : PUBLIC_IS_LOCAL === "TRUE"
  ? `http://localhost:3000/api/v1/`
  : `https://humblytics.onrender.com/api/v1/`

// ============================================================================
// Types
// ============================================================================

interface SnapshotPage {
  url: string
  title?: string
  description?: string
  screenshotUrl: string
  pageType?: string
  summary?: string
}

interface SiteSnapshot {
  propertyId: string
  domain: string
  status: "pending" | "ready" | "failed"
  capturedAt?: string
  pages: SnapshotPage[]
}

// ============================================================================
// Helpers
// ============================================================================

function authHeaders(token: string) {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }
}

// ============================================================================
// Site snapshot — sourced directly from heatmap pages
// ============================================================================

/**
 * Fetch the site snapshot for a property.
 * Uses heatmap pages as the source of truth for page URLs and screenshot URLs.
 * Returns null if no heatmap pages exist yet.
 */
export const getSiteSnapshot = async (
  propertyId: string,
  token: string
): Promise<SiteSnapshot | null> => {
  const res = await fetch(`${API_URL}heatmap-pages/${propertyId}`, {
    headers: authHeaders(token),
    credentials: "include",
  })
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`getSiteSnapshot failed: ${res.status}`)
  const data = await res.json()

  const rawPages: any[] = data?.data?.pages ?? []
  if (!rawPages.length) return null

  const pages: SnapshotPage[] = rawPages.map((p: any) => ({
    url: p.page_url,
    screenshotUrl: p.desktop_screenshot_url || p.mobile_screenshot_url || "",
  }))

  const hasScreenshots = pages.some((p) => p.screenshotUrl)

  return {
    propertyId,
    domain: "",
    status: hasScreenshots ? "ready" : "pending",
    pages,
  }
}

/**
 * Trigger screenshot capture for all heatmap pages that are missing one.
 * Fires capture requests in parallel and returns an updated snapshot.
 */
export const refreshSiteSnapshot = async (
  propertyId: string,
  token: string
): Promise<SiteSnapshot> => {
  const snapshot = await getSiteSnapshot(propertyId, token)
  const pages = snapshot?.pages ?? []

  // Trigger screenshot capture for pages missing a screenshot
  const missing = pages.filter((p) => !p.screenshotUrl)
  await Promise.allSettled(
    missing.map((p) =>
      fetch(`${API_URL}heatmap-pages/${propertyId}/screenshot`, {
        method: "POST",
        headers: authHeaders(token),
        credentials: "include",
        body: JSON.stringify({ pageUrl: p.url }),
      })
    )
  )

  // Re-fetch after triggering captures (screenshots may not be ready yet)
  const updated = await getSiteSnapshot(propertyId, token)
  return updated ?? { propertyId, domain: "", status: "pending", pages }
}

/**
 * Pick the screenshot a plan/preview surface should show as the control.
 * Prefers the page actually under test, then the site root, then the first page
 * that has one. Returns null while the crawl is still catching up so the caller
 * can keep its skeleton on screen instead of flashing an empty frame.
 */
export const pickSnapshotScreenshot = (
  snapshot: SiteSnapshot | null | undefined,
  preferredUrl?: string | null
): string | null => {
  const pages = (snapshot?.pages ?? []).filter((p) => Boolean(p.screenshotUrl))
  if (!pages.length) return null

  const normalize = (value: string) =>
    value.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/+$/, "")

  if (preferredUrl) {
    const wanted = normalize(preferredUrl)
    const exact = pages.find((p) => normalize(p.url) === wanted)
    if (exact) return exact.screenshotUrl
    const partial = pages.find(
      (p) => normalize(p.url).includes(wanted) || wanted.includes(normalize(p.url))
    )
    if (partial) return partial.screenshotUrl
  }

  const root = pages.find((p) => {
    try {
      return new URL(p.url).pathname === "/"
    } catch {
      return false
    }
  })
  return (root ?? pages[0]).screenshotUrl
}

// ============================================================================
// AI page analysis — split test recommendations
// ============================================================================

export interface PageAnalysisRecommendation {
  id: string
  title: string
  description?: string
  element_selector: string
  element_type?: string
  change_type: "text" | "css" | "layout" | "delete" | "duplicate"
  control_value?: string
  variant_value?: string
  reasoning?: string
  expected_impact?: string
  confidence_score?: number
  priority?: number
  implementation_effort?: string
}

export interface PageAnalysisResult {
  conversationId: string
  overall_analysis: string
  key_insights: string[]
  recommendations: PageAnalysisRecommendation[]
}

/**
 * Analyze a page with AI to get split test recommendations.
 * Uses POST /api/v1/ai/editor/analyze.
 */
const analyzePageForTests = async (
  propertyId: string,
  pageUrl: string,
  token: string,
  goal?: string
): Promise<PageAnalysisResult> => {
  const testId = `onboarding_${propertyId}_${Date.now()}`
  const res = await fetch(`${API_URL}ai/editor/analyze`, {
    method: "POST",
    headers: authHeaders(token),
    credentials: "include",
    body: JSON.stringify({ testId, propertyId, pageUrl, goal: goal ?? null }),
  })
  if (!res.ok) throw new Error(`analyzePageForTests failed: ${res.status}`)
  return await res.json()
}

// ============================================================================
// Draft
// ============================================================================

interface SuggestedTestDraftInput {
  propertyId: string
  pageUrl: string
  experimentName: string
  hypothesis?: string
  /**
   * A real ExperimentGoalId (form_submissions, click_through_rate, revenue...).
   * Derived from the goal the user picked in onboarding so the test measures
   * what they said they cared about. Previously the free-text hypothesis was
   * sent as testGoal, which stored prose in a field the backend validates as
   * an enum.
   */
  testGoal?: string
  instructions: ComponentInstruction[]
}

interface SuggestedTestDraftResult {
  draftId: string
  previewUrl: string
}

/**
 * Save instructions to the backend so hmbl.js can fetch them at preview time.
 * Uses PUT /api/v1/ab-tests/:testId with variant "B" and the instructions array.
 */
function serializeInstructions(instructions: ComponentInstruction[]) {
  return instructions.map((inst) => {
    const op = inst.operation as string
    const raw = inst.value as Record<string, unknown> | null
    let value: unknown = raw
    if (op === 'text' && raw && typeof raw === 'object') value = raw.newText ?? ''
    if (op === 'image' && raw && typeof raw === 'object') value = raw.newSrc ?? ''
    return { id: inst.id, selector: inst.selector, op, value, aiGenerated: inst.aiGenerated, aiReasoning: inst.aiReasoning }
  })
}

const createSuggestedTestDraft = async (
  input: SuggestedTestDraftInput,
  token: string
): Promise<SuggestedTestDraftResult> => {
  const draftId = `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  const res = await fetch(`${API_URL}ab-tests/${draftId}`, {
    method: "PUT",
    headers: authHeaders(token),
    credentials: "include",
    body: JSON.stringify({ variant: "B", instructions: serializeInstructions(input.instructions) }),
  })
  if (!res.ok) throw new Error(`createSuggestedTestDraft failed: ${res.status}`)
  return {
    draftId,
    previewUrl: buildDraftPreviewUrl(input.pageUrl, draftId, "B"),
  }
}

// ============================================================================
// Preview URL
// ============================================================================

/**
 * Build a preview URL that renders the site with the given draft's variant
 * applied. The installed Humblytics tracking script picks up the token and
 * applies instructions when the param is present.
 */
const buildDraftPreviewUrl = (
  pageUrl: string,
  draftId: string,
  variant: "A" | "B" = "B"
): string => {
  try {
    const u = new URL(pageUrl)
    // Use hash-based preview format that hmbl.js detects
    u.hash = `preview-humblytics-${draftId}`
    return u.toString()
  } catch {
    // Fallback for invalid URLs
    return `${pageUrl}#preview-humblytics-${draftId}`
  }
}

// ============================================================================
// Launch
// ============================================================================

// ============================================================================
// Before/After Preview Screenshots
// ============================================================================

export interface PreviewScreenshotResult {
  beforeUrl: string
  afterUrl: string
  verificationResults: Array<{ selector: string; op: string; applied: boolean | null }>
}

/**
 * Capture before/after screenshots via Firecrawl.
 * The backend applies each instruction via executeJavascript on the live page
 * and returns two screenshot URLs stored in R2.
 */
const capturePreviewScreenshots = async (
  propertyId: string,
  pageUrl: string,
  instructions: ComponentInstruction[],
  token: string
): Promise<PreviewScreenshotResult> => {
  const serialized = instructions.map((inst) => {
    const op = inst.operation as string
    const raw = inst.value as Record<string, unknown> | null
    let value: unknown = raw
    if (op === "text" && raw && typeof raw === "object") value = { newText: raw.newText ?? "" }
    if (op === "image" && raw && typeof raw === "object") value = { newSrc: raw.newSrc ?? "" }
    return { selector: inst.selector, op, value }
  })

  const externalApiUrl = API_URL.replace("/api/v1/", "/api/external/v1/")
  const res = await fetch(`${externalApiUrl}properties/${propertyId}/split-test-preview`, {
    method: "POST",
    headers: authHeaders(token),
    credentials: "include",
    body: JSON.stringify({ pageUrl, instructions: serialized }),
  })
  if (!res.ok) throw new Error(`capturePreviewScreenshots failed: ${res.status}`)
  return await res.json()
}

/**
 * Launch a suggested test by creating a live component A/B experiment.
 * Bypasses the draft concept — goes straight to experiment creation.
 */
const launchSuggestedTest = async (
  input: SuggestedTestDraftInput,
  token: string
): Promise<{ experimentId: string }> => {
  // Extract path from full URL (backend expects path-only for variants)
  let variantPath: string
  try {
    variantPath = new URL(input.pageUrl).pathname
  } catch {
    variantPath = input.pageUrl
  }

  const body = {
    testType: "component",
    experimentName: input.experimentName,
    testGoal: input.testGoal ?? "click_through_rate",
    variants: [
      { url: variantPath, isControl: true },
      { url: variantPath, instructions: serializeInstructions(input.instructions) },
    ],
  }

  const res = await fetch(`${API_URL}experiments/${input.propertyId}`, {
    method: "POST",
    headers: authHeaders(token),
    credentials: "include",
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw await createApiError(res)
  }
  const data = (await parseResponseBody(res)) as Record<string, unknown> | null
  return { experimentId: (data?.experiment_id as string) ?? "" }
}

// ============================================================================
// Background Experiment Generation — fire-and-forget job
// ============================================================================

export type ExperimentGenerationStatus = "pending" | "running" | "completed" | "failed"

export interface ExperimentGenerationJob {
  jobId: string
  status: ExperimentGenerationStatus
  attempt: number
  pageUrl: string
  enqueuedAt: string
  startedAt: string | null
  completedAt: string | null
  durationMs: number | null
  result: {
    analysis: PageAnalysisResult
    preview: PreviewScreenshotResult
    emailSentAt: string | null
  } | null
  error: { code: string; message: string } | null
}

/**
 * Enqueue background experiment generation for a property.
 * Idempotent — returns existing job if already pending/running/completed.
 * Pass force: true to re-enqueue after failure (or force a fresh run).
 */
export const triggerExperimentGeneration = async (
  propertyId: string,
  pageUrl: string,
  goal: string | null,
  token: string,
  force = false
): Promise<{ jobId: string; status: ExperimentGenerationStatus }> => {
  const res = await fetch(`${API_URL}property/${propertyId}/experiment-generation`, {
    method: "POST",
    headers: authHeaders(token),
    credentials: "include",
    body: JSON.stringify({ pageUrl, goal, source: "onboarding", force }),
  })
  if (!res.ok) throw new Error(`triggerExperimentGeneration failed: ${res.status}`)
  return await res.json()
}

/**
 * Get the latest experiment generation job for a property.
 * Returns null if no job has ever been enqueued (404).
 */
export const getExperimentGeneration = async (
  propertyId: string,
  token: string
): Promise<ExperimentGenerationJob | null> => {
  const res = await fetch(`${API_URL}property/${propertyId}/experiment-generation`, {
    headers: authHeaders(token),
    credentials: "include",
  })
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`getExperimentGeneration failed: ${res.status}`)
  return await res.json()
}

// ============================================================================
// Plan-step facts fallback + screenshot provenance
// ============================================================================

/**
 * What the plan step is actually able to show in the A frame. Reported on
 * `onboarding_plan_viewed` and `onboarding_plan_cta_clicked` as
 * `screenshot_state` so a plan drawn from a wireframe is never counted as a
 * plan drawn from the user's own page.
 *
 *  - `firecrawl` — the generation job's before-shot of the page under test
 *  - `snapshot`  — a heatmap-pages capture of the same site
 *  - `facts`     — no image, but the crawl returned quotable page facts
 *  - `none`      — nothing known yet, the skeleton page renders
 */
export type ScreenshotState = "firecrawl" | "snapshot" | "facts" | "none"

/**
 * Both possible image origins, kept apart so the event can tell them apart.
 * A bare string is read as the Firecrawl before-shot, because that is the only
 * single-source caller (the page passes `job.result.preview.beforeUrl`).
 */
export type ControlImageSources = {
  firecrawlUrl?: string | null
  snapshotUrl?: string | null
}

export type PageAnalysisKind = "headline" | "cta" | "block"

/**
 * Classify what part of the page a recommendation touches. The analysis gives
 * a CSS selector and a loose element type, never geometry, so this is the one
 * place that guess is made. `OnboardingTestCard.highlightKindFor` delegates
 * here so the drawn band and the facts panel can never disagree.
 */
export function recommendationKind(
  rec: Partial<
    Pick<PageAnalysisRecommendation, "element_type" | "element_selector">
  >
): PageAnalysisKind {
  const type = (rec.element_type ?? "").toLowerCase()
  if (type.includes("cta") || type.includes("button") || type.includes("link")) return "cta"
  if (type.includes("head") || type.includes("title") || /^h[1-6]$/.test(type)) return "headline"

  const selector = (rec.element_selector ?? "").toLowerCase()
  if (/(^|[\s,>])h[1-3]\b|headline|hero-title|\.title/.test(selector)) return "headline"
  if (/button|submit|a\[href|\.cta|add-to-cart|formkit/.test(selector)) return "cta"
  return "block"
}

/**
 * The quotable facts the crawl returned about the page. Only fields that exist
 * on `PageAnalysisResult` are used: recommendation `control_value` (what is on
 * the page today), `key_insights` and `overall_analysis`. Nothing is invented,
 * and an absent fact stays null rather than becoming placeholder prose.
 */
export type PageFacts = {
  /** The page's own headline or H1, as the crawl read it. */
  headline: string | null
  /** The primary CTA's current label. */
  cta: string | null
  /** A price found on the page, if one was found at all. */
  price: string | null
}

const PRICE_PATTERN =
  /(?:[$£€]\s?\d[\d,]*(?:\.\d{1,2})?|\b\d[\d,]*(?:\.\d{1,2})?\s?(?:USD|EUR|GBP)\b)(?:\s?\/\s?(?:mo|month|yr|year|user|seat))?/i

function tidyFact(value: string | null | undefined, max = 96): string | null {
  const text = (value ?? "").replace(/\s+/g, " ").trim()
  if (!text) return null
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

function findPrice(candidates: Array<string | null | undefined>): string | null {
  for (const candidate of candidates) {
    const match = (candidate ?? "").match(PRICE_PATTERN)
    if (match) return match[0].replace(/\s+/g, " ").trim()
  }
  return null
}

export function pageFactsFrom(
  analysis: PageAnalysisResult | null | undefined
): PageFacts {
  const recs = analysis?.recommendations ?? []
  const valueFor = (kind: PageAnalysisKind) =>
    tidyFact(
      recs.find((rec) => recommendationKind(rec) === kind && tidyFact(rec.control_value))
        ?.control_value
    )

  return {
    headline: valueFor("headline"),
    cta: valueFor("cta"),
    price: findPrice([
      ...recs.map((rec) => rec.control_value),
      ...(analysis?.key_insights ?? []),
      analysis?.overall_analysis,
    ]),
  }
}

/** True when the facts panel has at least one real line to show. */
export function hasPageFacts(facts: PageFacts | null | undefined): boolean {
  return Boolean(facts && (facts.headline || facts.cta || facts.price))
}

/**
 * Resolve what the plan step can show, in the order the user benefits from:
 * their page as captured for this test, then any capture of their site, then
 * their page's facts in text, then nothing.
 */
export function screenshotStateFor(
  controlUrl: string | ControlImageSources | null | undefined,
  analysis: PageAnalysisResult | null | undefined
): ScreenshotState {
  const sources: ControlImageSources =
    typeof controlUrl === "string" ? { firecrawlUrl: controlUrl } : controlUrl ?? {}

  if (sources.firecrawlUrl?.trim()) return "firecrawl"
  if (sources.snapshotUrl?.trim()) return "snapshot"
  if (hasPageFacts(pageFactsFrom(analysis))) return "facts"
  return "none"
}
